// DID Mappings Tab — CRUD management for external phone number → extension mappings
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Phone as PhoneIcon,
  CallReceived as InboundIcon,
} from '@mui/icons-material';
import { didMappingApi, locationsApi } from '../../services/apiClient';

const emptyMapping = {
  didNumber: '',
  label: '',
  targetExtension: '',
  locationId: null,
  enabled: true,
  notes: '',
};

export default function DidMappingsTab({ t }) {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [editData, setEditData] = useState({ ...emptyMapping });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);

  const loadMappings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await didMappingApi.getAll();
      setMappings(res.data?.data || []);
    } catch (err) {
      setError('Failed to load DID mappings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLocations = useCallback(async () => {
    try {
      const res = await locationsApi.getSites();
      const data = res.data;
      setLocations(Array.isArray(data) ? data : (data?.sites || data?.data || []));
    } catch {
      // Non-critical
    }
  }, []);

  useEffect(() => {
    loadMappings();
    loadLocations();
  }, [loadMappings, loadLocations]);

  const handleAdd = () => {
    setEditId(null);
    setEditData({ ...emptyMapping });
    setEditDialog(true);
  };

  const handleEdit = (mapping) => {
    setEditId(mapping.id);
    setEditData({
      didNumber: mapping.didNumber || mapping.did_number || '',
      label: mapping.label || '',
      targetExtension: mapping.targetExtension || mapping.target_extension || '',
      locationId: mapping.locationId || mapping.location_id || null,
      enabled: mapping.enabled !== false,
      notes: mapping.notes || '',
    });
    setEditDialog(true);
  };

  const handleSave = async () => {
    if (!editData.didNumber || !editData.targetExtension) return;
    setSaving(true);
    try {
      if (editId) {
        await didMappingApi.update(editId, editData);
      } else {
        await didMappingApi.create(editData);
      }
      setEditDialog(false);
      loadMappings();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to save';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      await didMappingApi.delete(deleteDialog.id);
      setDeleteDialog(null);
      loadMappings();
    } catch (err) {
      setError('Failed to delete DID mapping');
    }
  };

  const selectedLocation = locations.find(l => l.id === editData.locationId) || null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          <InboundIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1.2rem' }} />
          DID Mappings
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadMappings} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} size="small">
            Add DID
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Alert severity="info" variant="outlined" sx={{ mb: 2, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
        DID mappings route inbound calls from the 3CX trunk to internal extensions.
        When an external caller dials one of these numbers, Asterisk resolves it to the target extension
        and runs any matching call automations.
      </Alert>

      {mappings.length === 0 ? (
        <Alert severity="info">
          No DID mappings configured. Add a mapping to route external phone numbers to internal extensions.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>DID Number</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Target Extension</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mappings.map((m) => (
                <TableRow key={m.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PhoneIcon fontSize="small" color="action" />
                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                        {m.didNumber || m.did_number}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{m.label || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={m.targetExtension || m.target_extension}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {m.location?.friendlyName || m.location?.friendly_name || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={m.enabled ? 'Active' : 'Disabled'}
                      size="small"
                      color={m.enabled ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEdit(m)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeleteDialog(m)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Edit DID Mapping' : 'Add DID Mapping'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="DID Number"
              value={editData.didNumber}
              onChange={(e) => setEditData({ ...editData, didNumber: e.target.value })}
              placeholder="93302450"
              helperText="The external phone number (DID) as received from 3CX"
              required
              size="small"
            />
            <TextField
              label="Label"
              value={editData.label}
              onChange={(e) => setEditData({ ...editData, label: e.target.value })}
              placeholder="Main Reception"
              helperText="Friendly name for this DID (shown in call logs and automations)"
              size="small"
            />
            <TextField
              label="Target Extension"
              value={editData.targetExtension}
              onChange={(e) => setEditData({ ...editData, targetExtension: e.target.value })}
              placeholder="6001"
              helperText="Internal extension to route inbound calls to (e.g. 6001 for reception)"
              required
              size="small"
            />
            <Autocomplete
              options={locations}
              getOptionLabel={(opt) => opt.friendlyName || opt.friendly_name || `Location ${opt.id}`}
              value={selectedLocation}
              onChange={(_, val) => setEditData({ ...editData, locationId: val?.id || null })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Location (optional)"
                  size="small"
                  helperText="Associate this DID with a site for location-scoped automations"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />
            <TextField
              label="Notes"
              value={editData.notes}
              onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
              placeholder="From Telenet trunk, part of 3CX group..."
              multiline
              rows={2}
              size="small"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editData.enabled}
                  onChange={(e) => setEditData({ ...editData, enabled: e.target.checked })}
                />
              }
              label="Enabled"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !editData.didNumber || !editData.targetExtension}
          >
            {saving ? <CircularProgress size={20} /> : (editId ? 'Save' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete DID Mapping?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete the mapping for{' '}
            <strong>{deleteDialog?.didNumber || deleteDialog?.did_number}</strong>
            {deleteDialog?.label ? ` (${deleteDialog.label})` : ''}?
            Inbound calls to this number will no longer be routed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
